import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, X, Save, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EditableTagList = ({ title, description, items, onAdd, onRemove, onEdit, placeholder }) => {
  const [newValue, setNewValue] = useState('');
  const [editIndex, setEditIndex] = useState(-1);
  const [editValue, setEditValue] = useState('');

  const handleAdd = () => {
    const val = newValue.trim();
    if (val && !items.includes(val)) {
      onAdd(val);
      setNewValue('');
    }
  };

  const startEdit = (index) => {
    setEditIndex(index);
    setEditValue(items[index]);
  };

  const saveEdit = () => {
    const val = editValue.trim();
    if (val && editIndex >= 0) {
      onEdit(editIndex, val);
      setEditIndex(-1);
      setEditValue('');
    }
  };

  const cancelEdit = () => {
    setEditIndex(-1);
    setEditValue('');
  };

  return (
    <div className="bg-white border border-[#E5E5EA] rounded-xl p-5">
      <h3 className="font-semibold text-[#111111] mb-1">{title}</h3>
      <p className="text-sm text-[#6E6E73] mb-4">{description}</p>
      <div className="space-y-2 mb-4">
        {items.map((item, i) => (
          <div key={`${item}-${i}`} className="flex items-center gap-2 group">
            {editIndex === i ? (
              <>
                <Input value={editValue} onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveEdit(); } if (e.key === 'Escape') cancelEdit(); }}
                  className="flex-1" autoFocus />
                <Button size="sm" variant="outline" onClick={saveEdit} className="text-green-600">Salvar</Button>
                <Button size="sm" variant="outline" onClick={cancelEdit}>Cancelar</Button>
              </>
            ) : (
              <>
                <span className="flex-1 bg-[#F7F7F8] border border-[#E5E5EA] rounded-lg px-3 py-2 text-sm">{item}</span>
                <Button size="sm" variant="ghost" onClick={() => startEdit(i)} className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <Pencil className="w-3.5 h-3.5 text-[#6E6E73]" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => onRemove(i)} className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                </Button>
              </>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder={placeholder}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
          className="flex-1 max-w-sm" data-testid={`add-${title.toLowerCase().replace(/\s/g, '-')}-input`} />
        <Button type="button" variant="outline" onClick={handleAdd} data-testid={`add-${title.toLowerCase().replace(/\s/g, '-')}-button`}>
          <Plus className="w-4 h-4 mr-1" />Adicionar
        </Button>
      </div>
    </div>
  );
};

const SettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings`, { withCredentials: true });
      setSettings(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/settings`, settings, { withCredentials: true });
      toast.success('Configurações salvas com sucesso');
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  // Domain operations
  const addDomain = (val) => setSettings({ ...settings, allowed_domains: [...(settings.allowed_domains || []), val] });
  const removeDomain = (idx) => setSettings({ ...settings, allowed_domains: settings.allowed_domains.filter((_, i) => i !== idx) });
  const editDomain = (idx, val) => {
    const updated = [...settings.allowed_domains];
    updated[idx] = val;
    setSettings({ ...settings, allowed_domains: updated });
  };

  // Segment operations
  const addSegment = (val) => setSettings({ ...settings, client_segments: [...(settings.client_segments || []), val] });
  const removeSegment = (idx) => setSettings({ ...settings, client_segments: settings.client_segments.filter((_, i) => i !== idx) });
  const editSegment = (idx, val) => {
    const updated = [...settings.client_segments];
    updated[idx] = val;
    setSettings({ ...settings, client_segments: updated });
  };

  if (loading) {
    return <div className="flex justify-center p-12"><div className="w-12 h-12 border-4 border-[#AF2628] border-t-transparent rounded-full animate-spin"></div></div>;
  }

  if (!settings) return <div className="text-center p-12 text-[#6E6E73]">Erro ao carregar configurações</div>;

  return (
    <div className="max-w-3xl space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#111111]">Configurações</h1>
          <p className="text-[#6E6E73]">Configurações gerais do sistema</p>
        </div>
        <Button onClick={handleSave} className="bg-[#AF2628] hover:bg-[#961B22]" disabled={saving} data-testid="save-settings-button">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Salvando...' : 'Salvar Configurações'}
        </Button>
      </div>

      <EditableTagList
        title="Domínios Permitidos para Login"
        description="Usuários com estes domínios de e-mail podem acessar o sistema via Google Auth."
        items={settings.allowed_domains || []}
        onAdd={addDomain}
        onRemove={removeDomain}
        onEdit={editDomain}
        placeholder="Ex: empresa.com.br"
      />

      <EditableTagList
        title="Segmentos de Clientes"
        description="Segmentos disponíveis para classificar clientes privados."
        items={settings.client_segments || []}
        onAdd={addSegment}
        onRemove={removeSegment}
        onEdit={editSegment}
        placeholder="Ex: Tecnologia"
      />

      <div className="bg-[#F7F7F8] rounded-xl p-5 text-sm text-[#6E6E73]">
        <h3 className="font-semibold text-[#111111] mb-2">Informações do Sistema</h3>
        <div className="grid grid-cols-2 gap-2">
          <span>Versão:</span><span className="font-medium">ASI Docs v1.0</span>
          <span>Ambiente:</span><span className="font-medium">Produção</span>
          <span>Autenticação:</span><span className="font-medium">Google SSO</span>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
